import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ArrowRight, CheckCircle, XCircle, Shield, Bell, LayoutDashboard, Lock, Smartphone, Download, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TeamGrid } from "@/components/TeamGrid";
import { team } from "@/data/team";
import { useAnalytics } from "@/lib/analytics";

// Define types for our translation objects
interface HowItWorksStep {
  step: string;
  title: string;
  description: string;
}

interface BenefitItem {
  title: string;
  description: string;
}

// Main Index component
export default function Index({ apiHealthy }: { apiHealthy: boolean | null }) {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { trackButtonClick } = useAnalytics();
  
  // Apply RTL direction only on Index page when Hebrew is selected
  useEffect(() => {
    // Set RTL when Hebrew is selected
    document.documentElement.dir = i18n.language === 'he' ? 'rtl' : 'ltr';
    document.body.classList.toggle('rtl', i18n.language === 'he');
    
    // Clean up when component unmounts
    return () => {
      document.documentElement.dir = 'ltr';
      document.body.classList.remove('rtl');
    };
  }, [i18n.language]);

  const handleLogout = () => {
    logout();
  };

  const handleDashboardClick = () => {
    trackButtonClick('view_alerts_dashboard');
  };

  // Get arrays with proper typing
  const howItWorksSteps = t('howItWorks.steps', { returnObjects: true }) as HowItWorksStep[];
  const benefitItems = t('benefits.items', { returnObjects: true }) as BenefitItem[];
  
  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass fixed top-0 left-0 right-0 z-50 h-16 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img src="/mishu_svg.svg" alt="mishu" className="h-8 w-auto" />
        </div>
        <div className="flex items-center space-x-4">
          <LanguageSwitcher />
          {user && (
            <>
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                  {user.displayName ? user.displayName[0].toUpperCase() : user.email?.[0].toUpperCase()}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">
                    {user.displayName || user.email}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                aria-label="Logout"
              >
                <LogOut size={18} />
              </Button>
            </>
          )}
        </div>
      </header>

      <main className="flex-1 pt-24 px-6">
        <section className="max-w-6xl mx-auto pt-12 pb-20">
          <div className="flex flex-col items-center text-center mb-12 animate-fade-in">
            <h1 className="text-3xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-4 animate-slide-in">
              {t('app.heroSubtitle')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-4xl mb-8 leading-relaxed font-medium animate-fade-in">
              {t('app.description')}
            </p>
            
            {/* Statistics Section */}
            <div className="mb-12 max-w-6xl mx-auto">
              <div className="glass rounded-2xl p-8 md:p-12 bg-gradient-to-br from-red-50/50 to-orange-50/50 dark:from-red-950/20 dark:to-orange-950/20 border border-red-100/50 dark:border-red-800/30 shadow-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  {(t('statistics.items', { returnObjects: true }) as string[]).map((stat: string, index: number) => {
                    const parts = stat.split(' – ');
                    const number = parts[0];
                    const text = parts[1];
                    return (
                      <div
                      key={index}
                      className="p-6 rounded-xl bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm border border-white/30 dark:border-gray-700/40 shadow-md hover:shadow-lg transition-all duration-300 animate-fade-in"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                        <div className="text-center space-y-3">
                          <div className="text-3xl md:text-4xl lg:text-5xl font-black drop-shadow-sm" style={{ color: index % 3 === 0 ? '#F8A700' : index % 3 === 1 ? '#4DB26E' : '#58595B' }}>
                            {number}
                          </div>
                          <div className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                            {text}
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>
                
                {/* Mishu Description */}
                <div className="mt-12 text-center space-y-4 relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent rounded-xl"></div>
                  <div className="relative z-10 p-6">
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                      {t('statistics.mishu.title').split(' – ').map((part, index) => (
                        index === 0 ? (
                          <span key={index} className="text-primary">{part}</span>
                        ) : (
                          <span key={index} className="text-foreground"> – {part}</span>
                        )
                      ))}
                    </h3>
                    <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed font-medium">
                      {t('statistics.mishu.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <span className="text-xl md:text-2xl text-muted-foreground font-normal mb-6">
              {t('app.descriptionSubtext')}
            </span>
            <p className="text-xl text-muted-foreground max-w-3xl mb-8">
              {t('app.subtitle')}
            </p>
            {user ? (
              <div className="flex flex-col items-center gap-4">
                <Button asChild size="lg" className="bg-primary hover:opacity-90" onClick={handleDashboardClick}>
                  <Link to="/connections">
                    {t('actions.goToDashboard')}
                    <ArrowRight className="ml-2 h-5 w-5 rtl-flip" />
                  </Link>
                </Button>
                <p className="text-sm text-muted-foreground">
                  {t('helpers.alertsDelivery')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline">
                    <Link to="/login">{t('auth.login.label')}</Link>
                  </Button>
                  <Button asChild size="lg" className="bg-primary hover:opacity-90">
                    <Link to="/signup">
                      {t('actions.setupFromParent')}
                      <ArrowRight className="ml-2 h-5 w-5 rtl-flip" />
                    </Link>
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center max-w-md whitespace-pre-line">
                  {t('helpers.parentSetup')}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-16">
            {[
              {
                title: t('features.ai.title'),
                description: t('features.ai.description'),
                icon: Shield
              },
              {
                title: t('features.notifications.title'),
                description: t('features.notifications.description'),
                icon: Bell
              },
              {
                title: t('features.dashboard.title'),
                description: t('features.dashboard.description'),
                icon: LayoutDashboard
              },
              {
                title: t('features.privacy.title'),
                description: t('features.privacy.description'),
                icon: Lock
              },
            ].map((feature, index) => (
              <div 
                key={index} 
                className="glass rounded-xl p-6 animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary mb-4 mx-auto">
                  <feature.icon size={24} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-center">{feature.title}</h3>
                <p className="text-muted-foreground text-center">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-24">
            <h2 className="text-3xl font-bold text-center mb-12">{t('howItWorks.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {howItWorksSteps.map((step, index) => {
                const brandColors = ["#F8A700", "#4DB26E", "#58595B", "#F8A700"]; // orange, green, dark gray, repeat
                const bg = brandColors[index % brandColors.length];
                return (
                  <div 
                    key={index} 
                    className="flex flex-col items-center text-center animate-scale-in"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="h-12 w-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: bg }}>
                      <span className="text-white font-bold">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-24">
            <h2 className="text-3xl font-bold text-center mb-8">{t('benefits.title')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="glass rounded-xl p-6 flex items-start animate-scale-in">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-4">
                  <Bell size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{t('promo.instantNotifications.title')}</h3>
                  <p className="text-muted-foreground">{t('promo.instantNotifications.description')}</p>
                </div>
              </div>
              
              <div className="glass rounded-xl p-6 flex items-start animate-scale-in" style={{ animationDelay: '100ms' }}>
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-4">
                  <Smartphone size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{t('promo.oneTapAccess.title')}</h3>
                  <p className="text-muted-foreground">{t('promo.oneTapAccess.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefitItems.map((benefit, index) => (
                <div 
                  key={index} 
                  className="glass rounded-xl p-6 animate-scale-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <h3 className="text-xl font-semibold mb-3 text-center">{benefit.title}</h3>
                  <p className="text-muted-foreground text-center">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Meet the team - reusable component */}
          <div className="mt-24">
            <TeamGrid team={team} title={t('home.team.title')} showBio={false} />
          </div>
        </section>
      </main>

      <footer className="border-t py-6 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground mb-4 sm:mb-0">
            © {new Date().getFullYear()} Kinesis LTD. All rights reserved.
          </p>
          
          <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-semibold mb-4 sm:mb-0">
            {apiHealthy === null ? (
              <span className="animate-pulse">{t('status.checkingApi')}</span>
            ) : apiHealthy ? (
              <>
                <CheckCircle className="mr-1 h-3 w-3 text-green-500 rtl-flip" />
                {t('status.online')}
              </>
            ) : (
              <>
                <XCircle className="mr-1 h-3 w-3 text-red-500 rtl-flip" />
                {t('status.offline')}
              </>
            )}
          </span>
          
          <div className="flex gap-4">
            <Link 
              to="/about" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('footer.about')}
            </Link>
            <Link 
              to="/terms" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('footer.terms')}
            </Link>
            <Link 
              to="/privacy" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('footer.privacy')}
            </Link>
            <a 
              href="mailto:legal@knesis.com" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('footer.contact')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
